import React, {useEffect, useState} from "react";
import {Modal, Button, Upload, Spin, Empty, Tooltip} from "antd";
import {DeleteOutlined, EditOutlined, UploadOutlined} from "@ant-design/icons";
import StickerItem from "../../moodPack/sticker/StickerItem";
import {openNotificationError, openNotificationSuccess} from "../../../utils/Utils";
import apiFactory from "../../../api";
import {useInfoUser} from "../../../store/UserStore";
import {conversationType, MESSAGE_STATUS} from "../../../config/Constant";
import {useUploadHandler} from "../../../hooks/useUploadHandler";
import {GeneralModal} from "../GeneralModal";
import StickerKeywordModal from "./StickerKeywordModal";


export default function StickerPackStickersModal({open, onClose, pack}) {
    const {user} = useInfoUser();

    const [stickers, setStickers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalDeleteSticker, setIsModalDeleteSticker] = useState(false);
    const [stickerId, setStickerId] = useState(null);
    const [isKeywordModal, setIsKeywordModal] = useState(false);
    const [selectedSticker, setSelectedSticker] = useState(null);

    const {fileList, setFileList, uploading} = useUploadHandler(
        async (requests) => {
            return await apiFactory.stickerApi.uploadSticker(requests);
        },
        {
            debounceMs: 600,
            extraFields: {
                folder: conversationType.STICKER,
                userId: user?.userId,
                packId: pack?.packId,
                type: "sticker",
                contentType: MESSAGE_STATUS.STICKER,
            },
            onSuccess: async () => {
                const updatedList = await apiFactory.stickerApi.getStickerByPackId(pack?.packId);
                setStickers(updatedList?.data || []);
            },
        }
    );

    const handleDeleteSticker = async () => {
        if (!stickerId) return;

        try {
            const response = await apiFactory.stickerApi.deleteSticker(stickerId);

            if (response?.status === 200) {
                setStickers((prev) => prev.filter((s) => s.stickerId !== stickerId));
                openNotificationSuccess("Sticker deleted successfully!");
            } else {
                openNotificationError("Failed to delete sticker!");
            }
        } catch (error) {
            console.error("Delete sticker failed:", error);
            openNotificationError("An error occurred while deleting sticker!");
        }
    };

    const handleChange = (info) => {
        const valid = [];
        const invalid = [];


        info.fileList.forEach((item) => {
            const file = item.originFileObj;
            if (!file) return;

            if (stickers?.length >= 32) {
                invalid.push(`Exceeds the limit of 32 stickers`);
                return;
            }

            if (!file.type.startsWith("image/")) {
                invalid.push(`${file.name} is not an image file.`);
                return;
            }

            const maxSizeMB = 5;
            if (file.size / 1024 / 1024 > maxSizeMB) {
                invalid.push(`${file.name} exceeds the ${maxSizeMB}MB size limit.`);
                return;
            }

            valid.push(item);
        });

        if (invalid.length) {
            openNotificationError(invalid.join(", "));
        }

        setFileList(valid);
    };

    useEffect(() => {
        if (!pack?.packId) return;
        const fetchStickers = async () => {
            setLoading(true);
            try {
                const result = await apiFactory.stickerApi.getStickerByPackId(pack?.packId);
                if (result?.status === 200) {
                    setStickers(result.data || []);
                } else {
                    openNotificationError("Failed to load stickers");
                }
            } catch (err) {
                console.error("Failed to fetch stickers:", err);
                openNotificationError("Failed to load stickers");
            } finally {
                setLoading(false);
            }
        };
        fetchStickers();
    }, [pack?.packId]);

    return (
        <Modal
            width={960}
            title={pack?.name}
            open={open}
            onCancel={onClose}
            footer={null}
        >
            <div className="flex justify-end items-center my-4">
                <Upload
                    multiple
                    showUploadList={false}
                    beforeUpload={() => false}
                    onChange={handleChange}
                    fileList={fileList}
                    onRemove={() => setFileList([])}
                >
                    <Button
                        type="primary"
                        icon={<UploadOutlined/>}
                        loading={uploading}
                    >
                        Upload Sticker
                    </Button>
                </Upload>
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-40">
                    <Spin size="large"/>
                </div>
            ) : stickers.length === 0 ? (
                <Empty description="No stickers found"/>
            ) : (
                <div className="grid grid-cols-6 gap-3 mt-4">
                    {stickers.map((s) => (
                        <Tooltip title={s?.keywords?.join(", ") || "No keywords"} color={s?.keywords?.length > 0 ? 'geekblue' : 'gray'}>
                            <div
                                key={s.stickerId}
                                className="relative group flex flex-col items-center border rounded-lg p-2 shadow-sm hover:shadow-md transition-shadow duration-200"
                            >
                                <Tooltip title="Add keyword" color={'blue'} placement="right">
                                    <EditOutlined
                                        className="absolute top-2 right-2 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:text-blue-500 cursor-pointer text-lg"
                                        onClick={() => {
                                            setSelectedSticker(s);
                                            setIsKeywordModal(true);
                                        }}
                                    />
                                </Tooltip>

                                <Tooltip title="Delete sticker" color={'red'} placement="right">
                                    <DeleteOutlined
                                        onClick={() => {
                                            setStickerId(s.stickerId);
                                            setIsModalDeleteSticker(true);
                                        }}
                                        className="absolute top-8 right-2 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:text-red-500 cursor-pointer text-lg"
                                    />
                                </Tooltip>

                                <StickerItem
                                    resourceId={s.resourceId}
                                    frameCount={s.frameCount}
                                    size={s.width / s.frameCount > 100 ? 75 : s.width / s.frameCount}
                                />
                                <span className="mt-2 text-sm font-medium truncate w-full text-center">{s.name}</span>
                            </div>
                        </Tooltip>
                    ))}
                </div>
            )}

            {isKeywordModal &&
            <StickerKeywordModal
                open={isKeywordModal}
                sticker={selectedSticker}
                onClose={() => setIsKeywordModal(false)}
                onUpdated={(id, keywords) => {
                    setStickers(prev =>
                        prev.map(s =>
                            s.stickerId === id ? {...s, keywords} : s
                        )
                    );
                }}
            />
            }
            {isModalDeleteSticker &&
            <GeneralModal
                title={"Delete Sticker"}
                content={"You want to delete sticker?"}
                onCancel={() => setIsModalDeleteSticker(false)}
                open={isModalDeleteSticker}
                onConfirm={handleDeleteSticker}
            />}
        </Modal>
    );
}
